import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {SliderOptionsContainer} from '@Components/slider-options/components/slider-options-container/slider-options-container';
import {ControlledSlider} from '@Components/controlled-slider';
import {Text, TextSize} from '@Components/text';
import {InputCounter} from '@Components/input-counter';
import {InputFieldSize, InputFieldState} from '@Components/input-field-v2/input-field.types';
import styles from './exponential-slider-options.module.scss';
import type {ExponentialSliderOptionsProps} from './exponential-slider-options.types';

export function ExponentialSliderOptions({
  id = '',
  className = '',
  wrapText = false,
  inputCounterClassName = '',
  onChangeEnded = (): void => {},
  isDisabled = false,
  size = InputFieldSize.SMALL,
  state = InputFieldState.DEFAULT,
  isValueBlank = false,
  ...props
}: ExponentialSliderOptionsProps): ReactElement<any> {
  const [isChangingFromInputCounter, setIsChangingFromInputCounter] = useState<boolean>(false);
  const [tempInput, setTempInput] = useState<number>(props.value);

  const onSlideHandler = (value: number): void => {
    props.onChange(props.exponentialFunctionBackward(value));
  };

  const onSlideEndedHandler = (value: number): void => {
    onChangeEnded(props.exponentialFunctionBackward(value));
  };

  const getValueForSlider = (value: number): number => {
    return props.exponentialFunctionForward(value);
  };

  const getSlider = (): ReactElement<any> => {
    return (
      <ControlledSlider
        onSlideEnded={onSlideEndedHandler}
        onSlide={onSlideHandler}
        value={getValueForSlider(props.value)}
        min={props.range.min}
        max={props.range.max}
        step={0.01}
        isDisabled={isDisabled}
      />
    );
  };

  const onInputChange = (value: number): void => {
    if (!isChangingFromInputCounter) {
      setIsChangingFromInputCounter(true);
    }
    props.onChange(value);
    setTempInput(value);
  };

  const isValueOutOfBounds = (val: number): boolean => {
    return val < props.range.min || val > props.range.max;
  };

  const onInputChangeEnded = (value: number): void => {
    if (isValueOutOfBounds(value)) {
      if (value > props.range.max) {
        onChangeEnded(props.range.max);
      } else {
        onChangeEnded(props.range.min);
      }
    } else {
      onChangeEnded(value);
    }
    setIsChangingFromInputCounter(false);
  };

  const getValueForDisplay = (): number => {
    return isChangingFromInputCounter ? tempInput : Math.round(props.value);
  };

  const getUpperContainer = (): ReactElement<any> => {
    return (
      <>
        <Text
          val={props.label}
          size={TextSize.XSMALL}
          className={`${styles.text} ${wrapText ? styles.wrapText : styles.noWrap} ${isDisabled ? 'color-extra-light' : 'content-sub-text'} spacing-m-r-5`}
        />
        <InputCounter
          className={`${styles.inputCounter} ${inputCounterClassName}`}
          controlsDisabledStates={[getValueForDisplay() <= props.range.min, getValueForDisplay() >= props.range.max]}
          value={getValueForDisplay()}
          onInputChange={onInputChange}
          onInputChangeEnded={onInputChangeEnded}
          min={props.range.min}
          max={props.range.max}
          step={1}
          minimumStepFromInputField={1}
          isDisabled={isDisabled}
          elementTitle={props.label}
          state={state}
          size={size}
          isValueBlank={isValueBlank}
        />
      </>
    );
  };

  return <SliderOptionsContainer id={id} className={`${className} spacing-m-t-0`} upperContainer={getUpperContainer()} slider={getSlider()} />;
}
